import { api } from '@/api'
import type { UserResponse } from '@/api/users.types'
import type { User, UserInput } from '../types'
import { toRegisterRequest, toUpdateRequest, toUser } from '../transform/userTransformer'

// The IDP admin users API, reached through the BFF proxy (baseURL /bff). Each
// call returns DOMAIN types — the transformer is applied here so the wire shape
// stops at this boundary.
const BASE = '/v1/admin/users'

// A type (not interface) so it satisfies the key factory's Record<string,
// unknown> param bound.
export type ListUsersParams = {
  query?: string
  active?: boolean
  // SQL LIMIT/OFFSET, ordered by name ASC (server-side). The response stays a
  // bare array with no total count, so the screen pages by requesting one
  // extra row past `limit` and using its presence to decide whether a next
  // page exists.
  limit?: number
  offset?: number
}

// GET /v1/admin/users → UserResponse[] (a bare array, not wrapped).
export function listUsers(params?: ListUsersParams): Promise<User[]> {
  return api.get<UserResponse[]>(BASE, { params }).then((users) => users.map(toUser))
}

// GET /v1/admin/users/{id} → UserResponse. The edit screen also needs the
// user's raw `position` — dropped from the domain User type since the form
// doesn't show it — so an update can send it back unchanged; it rides along
// here rather than being added to the shared domain type.
export function getUser(id: string): Promise<User & { position: string }> {
  return api
    .get<UserResponse>(`${BASE}/${id}`)
    .then((response) => ({ ...toUser(response), position: response.position }))
}

// POST /v1/admin/users → UserResponse.
export function registerUser(input: UserInput): Promise<User> {
  return api.post<UserResponse>(BASE, { body: toRegisterRequest(input) }).then(toUser)
}

// PUT /v1/admin/users/{id} → UserResponse. `position` is the value fetched
// alongside the user (see getUser), not a form field.
export function updateUser(id: string, input: UserInput, position: string): Promise<User> {
  return api.put<UserResponse>(`${BASE}/${id}`, { body: toUpdateRequest(input, position) }).then(toUser)
}

// POST /v1/admin/users/{id}/status — enable/disable login.
export function setUserActive(id: string, active: boolean): Promise<void> {
  return api.post(`${BASE}/${id}/status`, { body: { status: active } }).then(() => undefined)
}

// DELETE /v1/admin/users/{id}/membership — detaches the user from the caller's
// tenant (tombstoned server-side, so it drops out of every list state, not
// just the active one). No body, no response payload. A 400 here carries a
// business-rule violation the caller needs to see — see membershipViolationCode.
export function removeUserMembership(id: string): Promise<void> {
  return api.delete(`${BASE}/${id}/membership`).then(() => undefined)
}
